import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Modal,
  Pagination,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconCloudUpload,
  IconPlugConnected,
  IconPlugConnectedX,
  IconRefresh,
  IconSearch,
  IconWand,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  confirmAutoMatch,
  deleteMapping,
  getAutoMatchSuggestions,
  getChannels,
  getConnections,
  getMappings,
  getRemoteProducts,
  syncPrices,
  upsertMapping,
} from "@/api/connections";
import { getBranches } from "@/api/branches";
import { getCategories } from "@/api/category";
import { getProducts } from "@/api/products";
import { notify } from "@/lib/notify";
import { useAccess } from "@/hooks/useAccess";
import { useCooldown } from "@/hooks/useCooldown";
import {
  ListCell,
  ListEmpty,
  ListHeader,
  ListRow,
  ListScroll,
} from "@/components/DataList";
import { PER_PAGE_OPTIONS, formatPrice } from "@/lib/format";
import { PageTitle } from "@/components/PageTitle";
import { EMPTY_LIST } from "@/lib/queryClient";

// Orphan'ların canlı fiyatı/kategorisi yok; sıralanacak değerleri olmadığı için null
// döner ve yön ne olursa olsun sonda kalırlar.
// Boolean sayıya çevriliyor: aksi halde karşılaştırıcı onu metne düşürüp 'false' < 'true' diye
// alfabetik sıralıyor — doğru sonucu tesadüfen veriyor ama Durum kolonu için niyet okunmuyor.
const sortValue = (row, key) => {
  if (row.kind !== "remote") {
    return null;
  }

  const value = row.remote[key] ?? null;

  return typeof value === "boolean" ? Number(value) : value;
};

// Matches the backend's remoteRefreshCooldownSeconds — the "Yenile" button self-disables for this
// long after a refresh so the POS isn't hammered (the backend enforces it too). A failed refresh uses
// the shorter error cooldown so a transient failure (e.g. cold-start relay 502) can be retried soon.
const REFRESH_COOLDOWN_SECONDS = 120;
const REFRESH_ERROR_COOLDOWN_SECONDS = 60;

// Menü ürünü hücresi. Kategori ve ad tek satırdayken kolonun 280px'ini paylaşıyor ve ikisi de
// kesiliyordu; alt alta konunca ad genişliğin tamamını kullanıyor, kategori üstte bağlam kalıyor.
// title yine de duruyor: çok uzun adlarda alt satır da kesilebilir, tam metin hover'da görünür.
function MenuProductLabel({ category, name }) {
  return (
    <Box
      style={{ flex: 1, minWidth: 0 }}
      title={category ? `${category} / ${name}` : name}
    >
      {category && (
        <Text size="xs" c="dimmed" lh={1.25} truncate>
          {category}
        </Text>
      )}
      <Text size="sm" fw={500} lh={1.25} truncate>
        {name}
      </Text>
    </Box>
  );
}

export default function Mappings() {
  // Eşleştirme kurmak/bozmak ve entegrasyon ürünlerini tazelemek connections:write; fiyat
  // göndermek prices:sync. Yetkisi olmayan listeyi görür ama değiştiremez.
  const { can } = useAccess();
  const canWrite = can("connections");
  const canSync = can("connections") || can("bulk");

  const { id } = useParams();
  const connectionId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { seconds: refreshCooldown, start: startRefreshCooldown } = useCooldown(
    `refresh-cooldown:${connectionId}`,
  );
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [suggestionOverrides, setSuggestionOverrides] = useState({});
  const [productSearch, setProductSearch] = useState("");
  // null = sırasız (varsayılan); { key, dir } kullanıcı kolon başlığına bastığında.
  const [sort, setSort] = useState(null);
  const [productsPage, setProductsPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(10);
  const [suggestionsPage, setSuggestionsPage] = useState(1);
  const [suggestionsPerPage, setSuggestionsPerPage] = useState(10);
  const [suggestOpened, { open: openSuggest, close: closeSuggest }] =
    useDisclosure(false);

  const { data: products = EMPTY_LIST } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });
  const { data: categories = EMPTY_LIST } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const { data: mappings = EMPTY_LIST, isLoading: mappingsLoading } = useQuery({
    queryKey: ["mappings", connectionId],
    queryFn: () => getMappings(connectionId),
  });
  const {
    data: remoteProducts = EMPTY_LIST,
    isLoading: remoteLoading,
    isError: remoteError,
  } = useQuery({
    queryKey: ["remote-products", connectionId],
    queryFn: () => getRemoteProducts(connectionId),
  });
  const { data: suggestions = EMPTY_LIST, isLoading: suggestionsLoading } =
    useQuery({
      queryKey: ["auto-match", connectionId],
      queryFn: () => getAutoMatchSuggestions(connectionId),
    });
  const { data: connections = EMPTY_LIST, isLoading: connectionsLoading } =
    useQuery({
      queryKey: ["connections"],
      queryFn: getConnections,
    });
  const { data: channels = EMPTY_LIST } = useQuery({
    queryKey: ["channels"],
    queryFn: getChannels,
  });
  const { data: branches = EMPTY_LIST } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranches,
  });

  // This page is scoped to one connection = one (branch, channel); surface which one so a match is
  // never confirmed against the wrong integration.
  const connection = connections.find((item) => item.id === connectionId);
  const branchName =
    branches.find((item) => item.id === connection?.branchId)?.name ?? "—";
  const channelName =
    channels.find((item) => item.id === connection?.channelId)?.name ?? "—";

  const productById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const categoryById = useMemo(
    () => new Map(categories.map((item) => [item.id, item.name])),
    [categories],
  );

  // Menü ürününü kategorisiyle etiketle: "Kategori / Ürün adı" (eşleşme hangi kategoriden belli olsun).
  // Kategori ve ad ayrı dönüyor: hücrede alt alta basılıyorlar (bkz. MenuProductLabel). Kategori
  // atılamaz — aynı ürün adı birden fazla kategoride geçebiliyor, tek başına ad hangi ürünle
  // eşleştiğini belirsiz bırakır.
  const menuParts = useCallback(
    (productId) => {
      const product = productById.get(productId);
      if (!product) {
        return { name: `#${productId}` };
      }
      return {
        category: categoryById.get(product.categoryId),
        name: product.name,
      };
    },
    [productById, categoryById],
  );

  // Aramanın taradığı düz metin: kullanıcı "kategori ürün" diye yazabildiği için birleşik hali.
  const menuLabel = useCallback(
    (productId) => {
      const { category, name } = menuParts(productId);

      return category ? `${category} / ${name}` : name;
    },
    [menuParts],
  );

  // Eşleştirilmemiş ürünler kategori bazlı gruplanır — eski sistemdeki gibi
  // select kutusunda kategori başlıkları altında listelenir.
  const unmappedProductOptions = useMemo(() => {
    const mappedIds = new Set(mappings.map((mapping) => mapping.productId));
    const available = products.filter((product) => !mappedIds.has(product.id));

    return categories
      .map((category) => ({
        group: category.name,
        items: available
          .filter((product) => product.categoryId === category.id)
          .sort((a, b) => a.rank - b.rank)
          .map((product) => ({
            value: String(product.id),
            label: product.name,
          })),
      }))
      .filter((group) => group.items.length > 0);
  }, [products, mappings, categories]);

  // Entegrasyondan gelen ürünler + entegrasyonda artık olmayan eşleştirmeler (ölü kayıt)
  // tek listede birleşir. Ölü kayıt tespiti listeye GÜVENEBİLDİĞİMİZDE anlamlı: istek
  // patlamışsa remoteProducts boş gelir ve her eşleştirme silinmeye aday görünürdü.
  const orphanMappings = useMemo(() => {
    if (remoteError) {
      return [];
    }
    const remoteIds = new Set(remoteProducts.map((remote) => remote.remoteId));
    return mappings.filter(
      (mapping) => !mapping.remoteId || !remoteIds.has(mapping.remoteId),
    );
  }, [mappings, remoteProducts, remoteError]);

  // Sırasız gelir — kullanıcı kolon başlığından sıralar.
  const productRows = useMemo(() => {
    const remote = remoteProducts.map((item) => ({
      kind: "remote",
      key: item.remoteId,
      remote: item,
    }));
    // Eşleşmemiş entegrasyon ürünleri en üstte — asıl iş onlar; eşleşenler ve orphan'lar altta.
    return [
      ...remote.filter((row) => !row.remote.mappedProductId),
      ...remote.filter((row) => row.remote.mappedProductId),
      ...orphanMappings.map((mapping) => ({
        kind: "orphan",
        key: `orphan-${mapping.id}`,
        mapping,
      })),
    ];
  }, [remoteProducts, orphanMappings]);

  const unmatchedCount = useMemo(
    () =>
      productRows.filter(
        (row) => row.kind === "remote" && !row.remote.mappedProductId,
      ).length,
    [productRows],
  );

  const filteredRows = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) {
      return productRows;
    }
    return productRows.filter((row) => {
      const remoteName =
        row.kind === "remote"
          ? row.remote.name
          : (row.mapping.remoteName ?? "");
      const productId =
        row.kind === "remote"
          ? row.remote.mappedProductId
          : row.mapping.productId;
      const menuName = productId ? menuLabel(productId) : "";
      return `${remoteName} ${menuName}`.toLowerCase().includes(term);
    });
  }, [productRows, productSearch, menuLabel]);

  const sortedRows = useMemo(() => {
    if (!sort) {
      return filteredRows;
    }

    const factor = sort.dir === "asc" ? 1 : -1;

    return [...filteredRows].sort((a, b) => {
      const left = sortValue(a, sort.key);
      const right = sortValue(b, sort.key);

      if (left === right) return 0;
      if (left == null) return 1;
      if (right == null) return -1;

      return (
        factor *
        (typeof left === "number"
          ? left - right
          : String(left).localeCompare(String(right), "tr"))
      );
    });
  }, [filteredRows, sort]);

  // Artan -> azalan -> sırasız; kullanıcı varsayılana dönebilsin.
  const toggleSort = (key) =>
    setSort((current) => {
      if (current?.key !== key) return { key, dir: "asc" };
      if (current.dir === "asc") return { key, dir: "desc" };
      return null;
    });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["mappings", connectionId] });
    queryClient.invalidateQueries({
      queryKey: ["remote-products", connectionId],
    });
    queryClient.invalidateQueries({ queryKey: ["auto-match", connectionId] });
  };

  const reloadMutation = useMutation({
    mutationFn: () => getRemoteProducts(connectionId, true),
    onSuccess: () => {
      notify.success("Entegrasyon ürünleri yenilendi");
      startRefreshCooldown(REFRESH_COOLDOWN_SECONDS);
      invalidateAll();
    },
    onError: () => {
      notify.error("Entegrasyon ürünleri alınamadı");
      startRefreshCooldown(REFRESH_ERROR_COOLDOWN_SECONDS);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (matches) => confirmAutoMatch(connectionId, matches),
    onSuccess: (result) => {
      notify.success(`${result.matched} ürün eşleştirildi`);
      setSelectedSuggestions([]);
      setSuggestionOverrides({});
      invalidateAll();
    },
    onError: () => notify.error("Eşleştirme başarısız"),
  });

  const manualMutation = useMutation({
    mutationFn: ({ productId, payload }) =>
      upsertMapping(connectionId, productId, payload),
    onSuccess: invalidateAll,
    onError: () => notify.error("Eşleştirme kaydedilemedi"),
  });

  const removeMutation = useMutation({
    mutationFn: (productId) => deleteMapping(connectionId, productId),
    onSuccess: () => {
      notify.success("Eşleştirme kaldırıldı");
      invalidateAll();
    },
    onError: () => notify.error("Kaldırılamadı"),
  });

  const pushMutation = useMutation({
    mutationFn: () => syncPrices(connectionId),
    // Hiç fiyat gitmediyse bu bir başarı değil: grup filtresi ya da eşleşmeyen ürünler
    // yüzünden olabilir ve yeşil tik partnere fiyatların kanalda güncellendiğini söyler.
    onSuccess: ({
      pushed,
      skipped,
      filtered = 0,
      noPrice = 0,
      missingRemoteIds = [],
      failed = [],
    }) => {
      invalidateAll();

      // Kanal reddetti (ör. Trendyol recurring-price) — genel mesaj, ham hatayı basma.
      if (failed.length) {
        notify.error(
          `${pushed} fiyat gönderildi, ${failed.length} ürün reddedildi`,
        );
        return;
      }

      const reasons = [
        noPrice ? `${noPrice} üründe ent. fiyat tanımlı değil` : null,
        filtered ? `${filtered} ürün grup filtresiyle hariç` : null,
        skipped ? `${skipped} fiyat zaten güncel` : null,
        missingRemoteIds.length
          ? `${missingRemoteIds.length} ürün entegrasyonda yok`
          : null,
      ].filter(Boolean);

      if (pushed) {
        notify.success([`${pushed} fiyat gönderildi`, ...reasons].join(", "));
      } else {
        notify.warning(
          reasons.length
            ? `Hiçbir fiyat gönderilmedi — ${reasons.join(", ")}`
            : "Hiçbir fiyat gönderilmedi",
        );
      }
    },
    onError: () => notify.error("Fiyat gönderimi başarısız"),
  });

  const busy = pushMutation.isPending || reloadMutation.isPending;
  const productsLoading = mappingsLoading || remoteLoading;

  const productsTotalPages = Math.max(
    1,
    Math.ceil(sortedRows.length / productsPerPage),
  );
  const pagedProductRows = sortedRows.slice(
    (productsPage - 1) * productsPerPage,
    productsPage * productsPerPage,
  );
  const suggestionsTotalPages = Math.max(
    1,
    Math.ceil(suggestions.length / suggestionsPerPage),
  );
  const pagedSuggestions = suggestions.slice(
    (suggestionsPage - 1) * suggestionsPerPage,
    suggestionsPage * suggestionsPerPage,
  );

  // Sıralama değişince de 1. sayfaya dön — kullanıcı sıralamayı üstten görmek ister.
  useEffect(() => {
    setProductsPage(1);
  }, [productsPerPage, productSearch, sortedRows.length, sort]);

  useEffect(() => {
    setSuggestionsPage(1);
  }, [suggestionsPerPage, suggestions.length]);

  const suggestionProductId = (suggestion) =>
    Number(suggestionOverrides[suggestion.remoteId] ?? suggestion.productId);

  const confirmSelected = () => {
    const matches = suggestions
      .filter((suggestion) => selectedSuggestions.includes(suggestion.remoteId))
      .map((suggestion) => ({
        productId: suggestionProductId(suggestion),
        remoteId: suggestion.remoteId,
        remoteName: suggestion.remoteName,
        price: suggestion.remotePrice,
        isActive: suggestion.remoteIsActive,
      }));
    if (matches.length) {
      confirmMutation.mutate(matches);
    }
  };

  // Sayfa TEK bir bağlantıya ait. Kimliği kiracının listesinde yoksa ortada eşleştirilecek bir
  // şey yok: eskiden başlıktaki şube/kanal rozetleri "—" olarak çiziliyor, alttaki üç istek 404
  // dönüyor ama "Entegrasyon Ürünlerini Yenile" ve "Fiyatları Gönder" tıklanabilir kalıyordu —
  // var olmayan bir entegrasyona fiyat göndermeyi teklif eden bir ekran.
  if (!connectionsLoading && !connection) {
    return (
      <Stack gap="lg">
        <PageTitle>Eşleştirme</PageTitle>
        {/* Kırmızı uyarı kutusu DEĞİL: bu bir arıza değil, ölü bir adres. Alarm rengi
                    kullanıcıya bir şeyin bozulduğunu ve düzeltilebileceğini söylüyor; oysa
                    yapılacak tek şey listeye dönmek. Panelde "gösterilecek bir şey yok" kalıbı
                    App.jsx'teki AccessError ile aynı: ortalı, sakin, tek çıkış. */}
        <Stack align="center" justify="center" gap="xs" mih="60vh">
          <ThemeIcon size={64} radius="xl" variant="light" color="gray">
            <IconPlugConnectedX size={30} stroke={1.5} />
          </ThemeIcon>
          <Text fw={600} fz="lg" mt="xs">
            Bağlantı bulunamadı
          </Text>
          <Text size="sm" c="dimmed" ta="center" maw={380}>
            {connectionId
              ? `#${connectionId} numaralı bağlantı kayıtlarda yok. Eşleştirme için önce listeden bir entegrasyon seç.`
              : "Geçersiz bağlantı adresi. Eşleştirme için listeden bir entegrasyon seç."}
          </Text>
          <Button
            mt="md"
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/connections")}
          >
            Entegrasyonlara dön
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageTitle>Eşleştirme</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconPlugConnected size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>Ürün Eşleştirme</Title>
            <Group gap="xs" mt={4} mb={2}>
              <Badge variant="light" color="chocolate">
                {branchName}
              </Badge>
              <Badge variant="light" color="grape">
                {channelName}
              </Badge>
            </Group>
            <Text c="dimmed" size="sm">
              Entegrasyon ürünlerini menü ürünleriyle eşleştir, fiyatları
              senkronla
            </Text>
          </div>
        </Group>
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/connections")}
          >
            Entegrasyonlar
          </Button>
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            loading={reloadMutation.isPending}
            disabled={!canWrite || busy || refreshCooldown > 0}
            onClick={() => reloadMutation.mutate()}
          >
            {refreshCooldown > 0
              ? `Entegrasyon Ürünlerini Yenile (${refreshCooldown}s)`
              : "Entegrasyon Ürünlerini Yenile"}
          </Button>
          <Button
            variant="light"
            color="grape"
            leftSection={<IconWand size={16} />}
            disabled={!canWrite || suggestions.length === 0}
            onClick={openSuggest}
          >
            Otomatik Eşleştirme ({suggestions.length})
          </Button>
          <Button
            leftSection={<IconCloudUpload size={16} />}
            loading={pushMutation.isPending}
            disabled={!canSync || busy}
            onClick={() => pushMutation.mutate()}
          >
            Fiyatları Gönder
          </Button>
        </Group>
      </Group>

      <Card withBorder radius="md" p="md">
        {!productsLoading && productRows.length > 0 && (
          <Group justify="center" mb="md">
            <TextInput
              w={280}
              size="xs"
              placeholder="Ürün ara"
              leftSection={<IconSearch size={14} />}
              value={productSearch}
              onChange={(event) => setProductSearch(event.currentTarget.value)}
            />
          </Group>
        )}
        {productsLoading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : remoteError ? (
          <Alert color="red" title="Entegrasyon ürünleri alınamadı" m="md">
            Bağlantı kontrol edilmeli. Eşleştirmeler doğrulanamadığı için
            listelenmiyor — boş liste entegrasyonda ürün olmadığı anlamına
            gelmez.
          </Alert>
        ) : productRows.length === 0 ? (
          <ListEmpty>Entegrasyondan ürün bulunamadı</ListEmpty>
        ) : (
          <>
            <Card withBorder radius="md" p={0}>
              <ListScroll minWidth={760}>
                <ListHeader
                  sort={sort}
                  onSort={toggleSort}
                  columns={[
                    { label: "Entegrasyon Ürünü", flex: true },
                    {
                      label: "Ent. Fiyat",
                      w: 110,
                      ta: "right",
                      sortKey: "price",
                    },
                    { label: "Durum", w: 90, sortKey: "isActive" },
                    { label: "Menü Ürünü", w: 280, ta: "left" },
                    { label: "İşlem", w: 90 },
                  ]}
                />
                {pagedProductRows.map((row) =>
                  row.kind === "remote" ? (
                    <ListRow key={row.key}>
                      <ListCell flex>
                        <Text size="sm" truncate>
                          {row.remote.name}
                        </Text>
                      </ListCell>
                      <ListCell w={110} ta="right">
                        <Text size="sm">{formatPrice(row.remote.price)}</Text>
                      </ListCell>
                      <ListCell w={90}>
                        <Badge
                          variant="light"
                          color={row.remote.isActive ? "teal" : "gray"}
                        >
                          {row.remote.isActive ? "satışta" : "kapalı"}
                        </Badge>
                      </ListCell>
                      <ListCell w={280} ta="left">
                        {row.remote.mappedProductId ? (
                          <MenuProductLabel
                            {...menuParts(row.remote.mappedProductId)}
                          />
                        ) : (
                          <Select
                            withCheckIcon={false}
                            size="xs"
                            searchable
                            w={260}
                            disabled={!canWrite}
                            placeholder="Ürün seç ve eşleştir"
                            data={unmappedProductOptions}
                            onChange={(value) => {
                              if (value) {
                                manualMutation.mutate({
                                  productId: Number(value),
                                  payload: {
                                    remoteId: row.remote.remoteId,
                                    remoteName: row.remote.name,
                                    price: row.remote.price,
                                    isActive: row.remote.isActive,
                                  },
                                });
                              }
                            }}
                          />
                        )}
                      </ListCell>
                      <ListCell w={90}>
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          color="red"
                          disabled={!row.remote.mappedProductId}
                          onClick={() =>
                            removeMutation.mutate(row.remote.mappedProductId)
                          }
                        >
                          Kaldır
                        </Button>
                      </ListCell>
                    </ListRow>
                  ) : (
                    <ListRow key={row.key}>
                      <ListCell flex>
                        <Text size="sm" c="dimmed" truncate>
                          {row.mapping.remoteName ?? "entegrasyonda yok"}
                        </Text>
                      </ListCell>
                      <ListCell w={110} ta="right">
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      </ListCell>
                      <ListCell w={90}>
                        <Badge variant="light" color="red">
                          yok
                        </Badge>
                      </ListCell>
                      <ListCell w={280} ta="left">
                        <MenuProductLabel
                          {...menuParts(row.mapping.productId)}
                        />
                      </ListCell>
                      <ListCell w={90}>
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          color="red"
                          onClick={() =>
                            removeMutation.mutate(row.mapping.productId)
                          }
                        >
                          Kaldır
                        </Button>
                      </ListCell>
                    </ListRow>
                  ),
                )}
                {pagedProductRows.length === 0 && (
                  <ListEmpty>Eşleşen ürün yok</ListEmpty>
                )}
              </ListScroll>
            </Card>
            <Group justify="space-between" mt="sm">
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Sayfa başı
                </Text>
                <Select
                  withCheckIcon={false}
                  w={75}
                  size="xs"
                  allowDeselect={false}
                  data={PER_PAGE_OPTIONS}
                  value={String(productsPerPage)}
                  onChange={(value) => setProductsPerPage(Number(value))}
                />
                <Text size="sm" c="dimmed">
                  {sortedRows.length} ürün
                  {unmatchedCount > 0 && ` · ${unmatchedCount} eşleşmemiş`}
                </Text>
              </Group>
              <Pagination
                withEdges
                total={productsTotalPages}
                value={productsPage}
                onChange={setProductsPage}
                size="sm"
              />
            </Group>
          </>
        )}
      </Card>

      <Modal
        opened={suggestOpened}
        onClose={closeSuggest}
        size="60rem"
        radius="md"
        title={
          <Group gap="sm">
            <ThemeIcon variant="light" color="grape" radius="md">
              <IconWand size={18} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="sm">
                Otomatik Eşleştirme Önerileri
              </Text>
              <Text size="xs" c="dimmed">
                İsim benzerliğine göre bulundu — onayladıkların eşleştirilir
              </Text>
            </div>
          </Group>
        }
      >
        {suggestionsLoading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : suggestions.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            Öneri bulunamadı
          </Text>
        ) : (
          <Stack gap="md">
            <Card withBorder radius="md" p={0}>
              <ListScroll minWidth={720} fit={false}>
                <Group
                  wrap="nowrap"
                  gap="md"
                  px="sm"
                  py="xs"
                  bg="var(--mantine-color-gray-0)"
                  style={{
                    borderBottom: "1px solid var(--mantine-color-gray-2)",
                  }}
                >
                  <Checkbox
                    aria-label="Tüm önerileri seç"
                    checked={selectedSuggestions.length === suggestions.length}
                    indeterminate={
                      selectedSuggestions.length > 0 &&
                      selectedSuggestions.length < suggestions.length
                    }
                    onChange={(event) => {
                      const { checked } = event.currentTarget;
                      setSelectedSuggestions(
                        checked ? suggestions.map((item) => item.remoteId) : [],
                      );
                    }}
                  />
                  <Text
                    size="xs"
                    fw={700}
                    c="dimmed"
                    tt="uppercase"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    Entegrasyon Ürünü
                  </Text>
                  <Text
                    size="xs"
                    fw={700}
                    c="dimmed"
                    tt="uppercase"
                    w={100}
                    ta="right"
                  >
                    Ent. Fiyat
                  </Text>
                  <Text
                    size="xs"
                    fw={700}
                    c="dimmed"
                    tt="uppercase"
                    w={300}
                    ta="left"
                  >
                    Menü Ürünü
                  </Text>
                  <Text
                    size="xs"
                    fw={700}
                    c="dimmed"
                    tt="uppercase"
                    w={80}
                    ta="center"
                  >
                    Benzerlik
                  </Text>
                </Group>
                {pagedSuggestions.map((suggestion) => (
                  <ListRow key={suggestion.remoteId}>
                    <Checkbox
                      aria-label={`${suggestion.remoteName} önerisini seç`}
                      checked={selectedSuggestions.includes(
                        suggestion.remoteId,
                      )}
                      onChange={(event) => {
                        const { checked } = event.currentTarget;
                        setSelectedSuggestions((prev) =>
                          checked
                            ? [...prev, suggestion.remoteId]
                            : prev.filter(
                                (item) => item !== suggestion.remoteId,
                              ),
                        );
                      }}
                    />
                    <ListCell flex>
                      <Text size="sm" truncate>
                        {suggestion.remoteName}
                      </Text>
                    </ListCell>
                    <ListCell w={100} ta="right">
                      <Text size="sm">
                        {formatPrice(suggestion.remotePrice)}
                      </Text>
                    </ListCell>
                    <ListCell w={300} ta="left">
                      <Select
                        aria-label={`${suggestion.remoteName} için menü ürünü`}
                        withCheckIcon={false}
                        size="xs"
                        searchable
                        w={290}
                        data={unmappedProductOptions}
                        value={String(suggestionProductId(suggestion))}
                        onChange={(value) => {
                          if (value) {
                            setSuggestionOverrides((prev) => ({
                              ...prev,
                              [suggestion.remoteId]: value,
                            }));
                          }
                        }}
                      />
                    </ListCell>
                    <ListCell w={80}>
                      <Badge
                        variant="light"
                        color={suggestion.rating >= 0.9 ? "teal" : "orange"}
                      >
                        %{Math.round(suggestion.rating * 100)}
                      </Badge>
                    </ListCell>
                  </ListRow>
                ))}
              </ListScroll>
            </Card>
            <Group justify="space-between">
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Sayfa başı
                </Text>
                <Select
                  withCheckIcon={false}
                  w={75}
                  size="xs"
                  allowDeselect={false}
                  data={PER_PAGE_OPTIONS}
                  value={String(suggestionsPerPage)}
                  onChange={(value) => setSuggestionsPerPage(Number(value))}
                />
                <Text size="sm" c="dimmed">
                  {suggestions.length} öneri
                </Text>
              </Group>
              <Pagination
                withEdges
                total={suggestionsTotalPages}
                value={suggestionsPage}
                onChange={setSuggestionsPage}
                size="sm"
              />
            </Group>
            <Group
              justify="space-between"
              pt="md"
              style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
            >
              <Text size="sm" c="dimmed">
                {selectedSuggestions.length} öneri seçildi
              </Text>
              <Button
                loading={confirmMutation.isPending}
                disabled={!selectedSuggestions.length}
                onClick={confirmSelected}
              >
                Seçilenleri Eşleştir ({selectedSuggestions.length})
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
